import { useEffect, useMemo, useState } from "react";
import {
  Badge,
  Button,
  Checkbox,
  Collapse,
  Group,
  Modal,
  Paper,
  Stack,
  Table,
  Text,
  Title,
} from "@mantine/core";
import { notifications } from "@mantine/notifications";
import { IconChevronDown, IconChevronRight, IconPackage, IconRefresh, IconTrash } from "@tabler/icons-react";
import { useTranslation } from "react-i18next";

import { ApiError, api } from "../../api/client";
import type { RmaListEntry } from "../../api/types";
import { ExportToSheetModal } from "../shipments/ExportToSheetModal";
import { MoveToReturnBoxModal } from "./MoveToReturnBoxModal";
import { ResponsiveTable } from "../responsiveTable/ResponsiveTable";

function firstNoteLine(notes: string | null | undefined): string | null {
  if (!notes) return null;
  const line = notes.split(/\r?\n/, 1)[0]?.trim();
  return line || null;
}

export function DefectsTab() {
  const { t } = useTranslation();
  const [devices, setDevices] = useState<RmaListEntry[]>([]);
  const [loading, setLoading] = useState(false);
  const [selected, setSelected] = useState<Set<number>>(new Set());
  const [collapsed, setCollapsed] = useState<Set<string>>(new Set());
  const [moveOpen, setMoveOpen] = useState(false);
  const [scrapOpen, setScrapOpen] = useState(false);
  const [scrapping, setScrapping] = useState(false);
  const [sheetOpen, setSheetOpen] = useState(false);

  async function load() {
    setLoading(true);
    try {
      const res = await api.listDevicesByStatus(["defect"], 500, { unassigned: true });
      setDevices(res.devices);
      setSelected((prev) => {
        const ids = new Set(res.devices.map((d) => d.id));
        const next = new Set<number>();
        for (const id of prev) if (ids.has(id)) next.add(id);
        return next;
      });
    } catch (err) {
      const detail = err instanceof ApiError ? JSON.stringify(err.detail) : String(err);
      notifications.show({ color: "red", message: detail.slice(0, 200) });
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    void load();
  }, []);

  // Group by batch_code (null → "—"), sorted by group key
  const groups = useMemo(() => {
    const map = new Map<string, RmaListEntry[]>();
    for (const d of devices) {
      const key = d.batch_code ?? "—";
      const arr = map.get(key);
      if (arr) arr.push(d);
      else map.set(key, [d]);
    }
    return [...map.entries()].sort((a, b) => a[0].localeCompare(b[0]));
  }, [devices]);

  function toggleOne(id: number) {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  }

  function toggleGroup(rows: RmaListEntry[]) {
    setSelected((prev) => {
      const next = new Set(prev);
      const allSelected = rows.every((d) => next.has(d.id));
      for (const d of rows) {
        if (allSelected) next.delete(d.id);
        else next.add(d.id);
      }
      return next;
    });
  }

  function toggleCollapse(key: string) {
    setCollapsed((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
  }

  async function doScrap() {
    if (selected.size === 0) return;
    setScrapping(true);
    try {
      const ids = Array.from(selected);
      const res = await api.scrapDevices(ids);
      notifications.show({
        color: res.failed.length === 0 ? "green" : "yellow",
        message: t("rma.defects.scrappedToast", {
          scrapped: res.scrapped.length,
          failed: res.failed.length,
        }),
      });
      if (res.failed.length > 0) {
        for (const f of res.failed.slice(0, 3)) {
          notifications.show({ color: "red", message: `#${f.id}: ${f.error}` });
        }
      }
      setScrapOpen(false);
      setSelected(new Set());
      void load();
    } catch (err) {
      const detail = err instanceof ApiError ? JSON.stringify(err.detail) : String(err);
      notifications.show({ color: "red", title: t("rma.saveFailedTitle"), message: detail.slice(0, 200) });
    } finally {
      setScrapping(false);
    }
  }

  return (
    <Stack>
      <Group justify="space-between">
        <Group gap="xs">
          <Title order={5}>{t("rma.defects.title")}</Title>
          <Badge color="red" variant="light">{devices.length}</Badge>
        </Group>
        <Group gap="xs">
          {selected.size > 0 && (
            <>
              <Text size="xs" c="dimmed">{t("rma.selectedCount", { count: selected.size })}</Text>
              <Button
                size="xs"
                color="blue"
                leftSection={<IconPackage size={12} />}
                onClick={() => setMoveOpen(true)}
              >
                {t("rma.bulkMoveToBox")}
              </Button>
              <Button
                size="xs"
                color="red"
                variant="light"
                leftSection={<IconTrash size={12} />}
                onClick={() => setScrapOpen(true)}
              >
                {t("rma.defects.scrap")}
              </Button>
            </>
          )}
          <Button
            size="xs"
            variant="light"
            disabled={devices.length === 0}
            onClick={() => setSheetOpen(true)}
          >
            {t("rma.defects.exportSheet")}
          </Button>
          <Button
            size="xs"
            variant="subtle"
            leftSection={<IconRefresh size={12} />}
            onClick={() => void load()}
            loading={loading}
          >
            {t("rma.reload")}
          </Button>
        </Group>
      </Group>

      {devices.length === 0 ? (
        <Text c="dimmed" size="sm">{t("rma.defects.empty")}</Text>
      ) : (
        groups.map(([key, rows]) => {
          const isCollapsed = collapsed.has(key);
          const allSelected = rows.every((d) => selected.has(d.id));
          const someSelected = rows.some((d) => selected.has(d.id));
          return (
            <Paper key={key} withBorder p="sm">
              <Group justify="space-between" mb="xs">
                <Group gap="xs" style={{ cursor: "pointer" }} onClick={() => toggleCollapse(key)}>
                  {isCollapsed ? <IconChevronRight size={16} /> : <IconChevronDown size={16} />}
                  <Title order={6}>{t("rma.defects.batchGroup", { code: key })}</Title>
                  <Badge variant="light">{rows.length}</Badge>
                </Group>
                <Checkbox
                  checked={allSelected}
                  indeterminate={!allSelected && someSelected}
                  onChange={() => toggleGroup(rows)}
                  aria-label={t("rma.defects.selectGroup", { code: key })}
                />
              </Group>
              <Collapse in={!isCollapsed}>
                <ResponsiveTable
                  stickyColumns={2}
                  mobile={{
                    rows,
                    rowKey: (d) => d.id,
                    title: (d) => <Text span ff="monospace" size="sm" fw={600}>{d.dev_eui.toLowerCase()}</Text>,
                    subtitle: (d) => d.updated_at ? new Date(d.updated_at).toLocaleString() : undefined,
                    fields: [
                      { label: t("rma.colModel"), render: (d) => d.model ?? "—" },
                      { label: t("rma.colSerial"), render: (d) => d.serial_number ?? "—" },
                      { label: t("rma.colReason"), render: (d) => firstNoteLine(d.notes) ?? "—" },
                    ],
                    selection: {
                      isSelected: (d) => selected.has(d.id),
                      toggle: (d) => toggleOne(d.id),
                      allSelected,
                      someSelected: !allSelected && someSelected,
                      toggleAll: () => toggleGroup(rows),
                    },
                  }}
                >
                <Table.ScrollContainer minWidth={620}>
                  <Table striped highlightOnHover>
                    <Table.Thead>
                      <Table.Tr>
                        <Table.Th w={36} />
                        <Table.Th>DEV_EUI</Table.Th>
                        <Table.Th>{t("rma.colModel")}</Table.Th>
                        <Table.Th>{t("rma.colSerial")}</Table.Th>
                        <Table.Th>{t("rma.colReason")}</Table.Th>
                        <Table.Th w={150}>{t("rma.colUpdated")}</Table.Th>
                      </Table.Tr>
                    </Table.Thead>
                    <Table.Tbody>
                      {rows.map((d) => (
                        <Table.Tr key={d.id}>
                          <Table.Td>
                            <Checkbox
                              checked={selected.has(d.id)}
                              onChange={() => toggleOne(d.id)}
                              aria-label={t("rma.selectOne", { eui: d.dev_eui })}
                            />
                          </Table.Td>
                          <Table.Td>
                            <Text ff="monospace" size="sm">{d.dev_eui.toLowerCase()}</Text>
                          </Table.Td>
                          <Table.Td><Text size="sm">{d.model ?? "—"}</Text></Table.Td>
                          <Table.Td><Text size="sm">{d.serial_number ?? "—"}</Text></Table.Td>
                          <Table.Td>
                            <Text size="xs" c="dimmed" lineClamp={1} maw={260}>
                              {firstNoteLine(d.notes) ?? "—"}
                            </Text>
                          </Table.Td>
                          <Table.Td>
                            <Text size="xs" c="dimmed">
                              {d.updated_at ? new Date(d.updated_at).toLocaleString() : "—"}
                            </Text>
                          </Table.Td>
                        </Table.Tr>
                      ))}
                    </Table.Tbody>
                  </Table>
                </Table.ScrollContainer>
                </ResponsiveTable>
              </Collapse>
            </Paper>
          );
        })
      )}

      <MoveToReturnBoxModal
        opened={moveOpen}
        onClose={() => setMoveOpen(false)}
        deviceIds={Array.from(selected)}
        onMoved={() => {
          setSelected(new Set());
          void load();
        }}
      />

      <ExportToSheetModal
        opened={sheetOpen}
        onClose={() => setSheetOpen(false)}
        target="defects"
        ids={devices.map((d) => d.id)}
      />

      <Modal opened={scrapOpen} onClose={() => setScrapOpen(false)} title={t("rma.defects.scrapConfirmTitle")} centered>
        <Stack>
          <Text size="sm">{t("rma.defects.scrapConfirmBody", { count: selected.size })}</Text>
          <Group justify="flex-end">
            <Button variant="subtle" onClick={() => setScrapOpen(false)}>{t("common.cancel")}</Button>
            <Button color="red" loading={scrapping} leftSection={<IconTrash size={16} />} onClick={() => void doScrap()}>
              {t("common.confirm")}
            </Button>
          </Group>
        </Stack>
      </Modal>
    </Stack>
  );
}
